import {
  AdvancedFilterParams,
  AdvancedFilterType,
  FilterType,
} from 'talent_matching/types';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { formatCurrency } from 'shared/utils/currency';
import { formatShortDate } from 'talent_search/lib/utils';
import uuid from 'uuid';
import { withLocalePrefix } from 'shared/utils/translation';
import escape from 'lodash/escape';

const t = withLocalePrefix('talent_matching.filters.operator_labels');

class AdvancedFilter implements AdvancedFilterType {
  type: FilterType | null;
  operator: Item | null;
  value: Item | Item[] | string | null;
  reactKey: string;

  constructor(obj?: AdvancedFilterType) {
    this.type = obj?.type || null;
    this.operator = obj?.operator || null;
    this.value = obj?.value || null;
    this.reactKey = obj?.reactKey || uuid.v4();
  }

  queryKey(): keyof AdvancedFilterParams | null {
    const operator = this.operator?.value || '';
    if (
      ['is_defined', 'is_not_defined'].includes(operator) &&
      this.type?.value
    ) {
      return `defined_${this.type.value}` as keyof AdvancedFilterParams;
    }

    if (!(this.type && this.operator && this.value)) {
      return null;
    }

    let key = this.type.value;
    if (key === 'candidate_tag_id') {
      if (this.operator.value === 'has_any') {
        key = 'any_candidate_tag_id';
      }
      if (this.operator.value === 'has_none') {
        key = 'without_candidate_tag_id';
      }
    }

    if (key === 'source_id') {
      if (this.operator.value === 'has_none') {
        key = 'without_source_id';
      }
    }

    if (
      this.operator.value === 'contains' &&
      !['short_text', 'long_text'].includes(this.type.valueType)
    ) {
      if (key.includes('_id')) {
        key = key.replace('_id', '_contains');
      } else {
        key = `txt_${key}`;
      }
    }

    return key as keyof AdvancedFilterParams;
  }

  queryParamValue(): string[] | '1' | '0' | null {
    const operator = this.operator?.value || '';
    if (
      ['is_defined', 'is_not_defined'].includes(operator) &&
      this.type?.value
    ) {
      return operator === 'is_defined' ? '1' : '0';
    }

    if (!(this.type && this.operator && this.value)) {
      return null;
    }

    if (typeof this.value === 'string') {
      const trimmedValue = this.value.trim();
      const emptyValues = ['', ':'];

      if (emptyValues.includes(trimmedValue)) {
        return null;
      }

      if (this.type.valueType === 'currency') {
        const [min, max] = (this.value.split('$')[1] || ':').split(':');
        if (!min.trim() && !max.trim()) {
          return null;
        }
      }

      return [trimmedValue];
    }

    if (Array.isArray(this.value)) {
      if (this.value.length === 0) {
        return null;
      }

      const values = (this.value as Item[])
        .filter((item) => item?.value !== null && item?.value !== undefined)
        .map((item) => item.value.toString());

      return values.length ? values : null;
    }

    const single = (this.value as Item | null)?.value;
    return single !== null && single !== undefined ? [single.toString()] : null;
  }

  applied(): boolean {
    return Boolean(this.queryKey() && this.queryParamValue());
  }

  operatorText(): string | undefined {
    if (this.operator?.value === 'included') {
      return undefined;
    }

    return this.operator?.label;
  }

  formatSelections(userDateFormat: string): string | null {
    const operator = this.operator?.value || '';
    const filterValue = this.value;
    if (
      ['is_defined', 'is_not_defined'].includes(operator) &&
      this.type?.value
    ) {
      return null;
    }

    let textValue: string;

    if (typeof filterValue === 'string') {
      textValue = escape(filterValue);

      if (operator === 'between') {
        if (this.type?.valueType === 'date') {
          const [start, end] = textValue.split(':');
          const startFormatted =
            formatShortDate(start, userDateFormat) || '...';
          const endFormatted = formatShortDate(end, userDateFormat) || '...';
          textValue = `${startFormatted} - ${endFormatted}`;
        } else {
          textValue = textValue
            .replace(/^:/, '... - ')
            .replace(/:$/, ' - ...')
            .replace(/:/g, ' - ');
        }
      } else if (
        ['less_than', 'greater_than', 'on_or_after', 'on_or_before'].includes(
          operator
        )
      ) {
        const dateValue = textValue.replace(/:/, '');
        if (this.type?.valueType === 'date') {
          textValue = formatShortDate(dateValue, userDateFormat) || dateValue;
        } else {
          textValue = dateValue;
        }
      } else if (operator === 'equal_to') {
        const dateValue = textValue.split(':')[0];
        if (this.type?.valueType === 'date') {
          textValue = formatShortDate(dateValue, userDateFormat) || dateValue;
        } else {
          textValue = dateValue;
        }
      }
    } else if (Array.isArray(filterValue)) {
      textValue = filterValue.map((item) => escape(item.label)).join(', ');
    } else {
      textValue = escape(filterValue?.label as string);
    }

    if (this.type?.valueType === 'currency') {
      const [currencyCode, value] = textValue.split('$');
      textValue = formatCurrency(Number(value), currencyCode);
    }

    return textValue;
  }

  appliedFilterText(userDateFormat: string): string | null {
    if (!this.type || !this.operator || !this.operator.value) {
      return null;
    }

    const subject = this.type.label;
    const selections = this.formatSelections(userDateFormat);

    if (['is_defined', 'is_not_defined'].includes(this.operator.value)) {
      return t(this.operator.value, { subject });
    }

    if (!selections) {
      return null;
    }

    return t(this.operator.value, { subject, selections });
  }
}

export default AdvancedFilter;
